/**
 * The demo crate.
 *
 * Each record is a deterministic arrangement: chords as semitone offsets from
 * the root (one per bar) plus 16-step patterns for the drums, bass and arp.
 * Because the music is a pure function of musical position, seeking works
 * exactly like seeking a file.
 */

import type { DemoTrack } from '../../types/demo.ts';

export const DEMO_TRACKS: readonly DemoTrack[] = [
  {
    id: 'demo-1',
    title: 'Glass Meridian',
    artist: 'Aurora Field',
    album: 'Northern Optics',
    bpm: 84,
    root: 57,
    duration: 196,
    style: 'pad',
    prog: [
      [0, 3, 7, 14],
      [-4, 0, 3, 10],
      [5, 8, 12, 15],
      [-2, 2, 5, 12],
    ],
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hat: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 1, 1, 0],
    bass: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0],
    arp: [1, 0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 1, 0, 1, 0],
    palette: { hue: 196, sat: 0.52, style: 'halo' },
  },
  {
    id: 'demo-2',
    title: 'Velvet Static',
    artist: 'The Paper Moons',
    album: 'Slow Exposure',
    bpm: 96,
    root: 53,
    duration: 174,
    style: 'pluck',
    prog: [
      [0, 4, 7, 11],
      [-3, 2, 5, 9],
      [-5, 0, 4, 7],
      [2, 5, 9, 12],
    ],
    kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1],
    hat: [1, 0, 1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 0],
    bass: [1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0],
    arp: [1, 0, 0, 1, 1, 0, 1, 0, 0, 1, 1, 0, 1, 0, 0, 1],
    palette: { hue: 28, sat: 0.58, style: 'strata' },
  },
  {
    id: 'demo-3',
    title: 'Neon Ceramic',
    artist: 'Kite & Compass',
    album: 'Blue Hour Club',
    bpm: 118,
    root: 45,
    duration: 208,
    style: 'saw',
    prog: [
      [0, 3, 7, 10],
      [5, 8, 12, 15],
      [3, 7, 10, 14],
      [-2, 3, 7, 10],
    ],
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hat: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 1],
    bass: [1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 0],
    arp: [1, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1],
    palette: { hue: 322, sat: 0.62, style: 'prism' },
  },
  {
    id: 'demo-4',
    title: 'Low Tide Radio',
    artist: 'Marisol Vega',
    album: 'Saltwater Tapes',
    bpm: 72,
    root: 50,
    duration: 188,
    style: 'pad',
    prog: [
      [0, 5, 9, 12],
      [-2, 3, 7, 10],
      [-4, 0, 5, 9],
      [-7, 0, 4, 7],
    ],
    kick: [1, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hat: [1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0],
    bass: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0],
    arp: [0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0],
    palette: { hue: 168, sat: 0.44, style: 'wave' },
  },
  {
    id: 'demo-5',
    title: 'Porcelain Drive',
    artist: 'Nakamura Blue',
    album: 'Midnight Chrome',
    bpm: 124,
    root: 47,
    duration: 216,
    style: 'saw',
    prog: [
      [0, 3, 7, 12],
      [-5, 2, 5, 10],
      [-2, 3, 7, 10],
      [3, 7, 10, 15],
    ],
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 1],
    snare: [0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0],
    hat: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    bass: [1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 0, 1, 0, 1],
    arp: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    palette: { hue: 258, sat: 0.6, style: 'grid' },
  },
  {
    id: 'demo-6',
    title: 'Paper Lanterns',
    artist: 'Emi Sato',
    album: 'Quiet Machines',
    bpm: 88,
    root: 55,
    duration: 182,
    style: 'pluck',
    prog: [
      [0, 4, 9, 11],
      [-2, 2, 7, 9],
      [-4, 0, 5, 7],
      [-5, 2, 4, 9],
    ],
    kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hat: [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    bass: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0],
    arp: [1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1],
    palette: { hue: 8, sat: 0.5, style: 'orbit' },
  },
];
